"use client";

import { cn } from "@/lib/utils";
import { useState, useRef, useEffect, useMemo } from "react";
import { ChevronRightIcon } from "lucide-react";
import { motion } from "motion/react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { TextShimmer } from "../motion-primitives/text-shimmer";
import type { ChatStatus } from "ai";

const headerStyles = "text-xs text-muted-foreground/90 font-normal select-none";

export type ToolCallsGroupProps = {
  isInProgress: boolean;
  hasVersion: boolean;
  chatStatus: ChatStatus;
  isLatestMessage: boolean;
  children: React.ReactNode;
  className?: string;
};

export function ToolCallsGroup({
  isInProgress,
  hasVersion,
  chatStatus,
  isLatestMessage,
  children,
  className,
}: ToolCallsGroupProps) {
  // When in progress, default to expanded. When done, default to collapsed.
  const [isOpen, setIsOpen] = useState(isLatestMessage && isInProgress);

  // Track if user manually toggled the state
  const userToggledRef = useRef(false);
  const prevChatStatusRef = useRef(chatStatus);
  const prevIsLatestRef = useRef(isLatestMessage);

  // When user manually toggles, mark it
  const handleOpenChange = (open: boolean) => {
    userToggledRef.current = true;
    setIsOpen(open);
  };

  useEffect(() => {
    // When work starts (no version yet, or new work), expand
    if (isInProgress && !hasVersion) {
      setIsOpen(true);
      userToggledRef.current = false; // Reset user toggle flag when new work starts
    }

    // Collapse when chat transitions to "ready", has version, is latest message, and user hasn't toggled
    const chatBecameReady =
      prevChatStatusRef.current !== "ready" && chatStatus === "ready";
    if (
      chatBecameReady &&
      hasVersion &&
      isLatestMessage &&
      !userToggledRef.current
    ) {
      setIsOpen(false);
    }

    // Also collapse when this message stops being the latest (user sent new message)
    if (
      prevIsLatestRef.current &&
      !isLatestMessage &&
      hasVersion &&
      !userToggledRef.current
    ) {
      setIsOpen(false);
    }

    prevChatStatusRef.current = chatStatus;
    prevIsLatestRef.current = isLatestMessage;
  }, [hasVersion, chatStatus, isInProgress, isLatestMessage]);

  // Only apply chat status check for the latest message
  // For older messages, just check if tool calls are in progress
  const isWorkInProgress = isLatestMessage
    ? isInProgress || chatStatus === "streaming" || chatStatus === "submitted"
    : isInProgress;
  const headerText = isWorkInProgress ? "Making changes..." : "Changes applied";

  // Memoize the shimmer element to prevent re-creating on every render
  const headerContent = useMemo(() => {
    if (isWorkInProgress) {
      return <TextShimmer as="span">{headerText}</TextShimmer>;
    }
    return headerText;
  }, [isWorkInProgress, headerText]);

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={handleOpenChange}
      className={cn(headerStyles, className)}
    >
      <CollapsibleTrigger className="cursor-pointer flex items-start gap-1.5">
        <motion.div
          animate={{ rotate: isOpen ? 90 : 0 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
          className="mt-0.5"
        >
          <ChevronRightIcon className="size-3" />
        </motion.div>
        <div className={headerStyles}>{headerContent}</div>
      </CollapsibleTrigger>
      <motion.div
        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="overflow-hidden relative"
      >
        {/* Vertical line connecting chevron to tool calls */}
        <div className="absolute left-[5px] top-2 bottom-2 w-px bg-border" />

        <CollapsibleContent
          className="text-[11px] font-mono font-normal text-muted-foreground/80 flex flex-col gap-1 mt-2 mb-2 ml-[18px]"
          forceMount
        >
          {children}
        </CollapsibleContent>
      </motion.div>
    </Collapsible>
  );
}
